import { useCallback, useRef } from "react";

export const useThrottleCallback = <T extends (...args: any[]) => void>(
  callback: T,
  delay: number,
) => {
  const lastExecutedRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const throttledCallback = useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();
      const timeSinceLastExecution = now - lastExecutedRef.current;

      if (timeSinceLastExecution >= delay) {
        // Execute immediately if enough time has passed
        lastExecutedRef.current = now;
        callback(...args);
      } else {
        // Schedule execution for the remaining time
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          lastExecutedRef.current = Date.now();
          callback(...args);
        }, delay - timeSinceLastExecution);
      }
    },
    [callback, delay],
  );

  return throttledCallback;
};
